import React from "react";

import { Star } from "~/components/star";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";
import { Permission } from "~/models/dto";

import { usePermissions } from "../common/use-permissions";
import { useUserProfile } from "../common/use-user-profile";
import { DashboardFilter } from "../routes/dashboards/dashboard-filter";
import { DASHBOARDS_OVERVIEW_PATH } from "../routes/Routes";

import { LinkTo } from "./react/link";
import { useErrorMessage } from "./_notification-error";

export interface DashboardStarProps {
	id: undefined | number;
	className?: string;
}

export const DashboardStar: React.FC<DashboardStarProps> = ({ id, ...props }) => {
	const permissions = usePermissions();
	const { displayErrorMessage } = useErrorMessage();
	const [profile, updateUserProfile] = useUserProfile({
		onError: (error) => {
			displayErrorMessage({
				message: "Something went wrong while starring a dashboard",
				error,
			});
		},
	});

	// starred={undefined} renders a skeleton star
	if (id == null || profile == null || permissions == null) {
		return <Star {...props} starred={undefined} />;
	}

	const hasDeleteFavoriteDashboardsPermission = permissions.hasSystemPermission(Permission.DeleteFavoriteDashboards);
	const hasCreateFavoriteDashboardsPermission = permissions.hasSystemPermission(Permission.CreateFavoriteDashboards);

	const onStarredChange = (next: boolean) => {
		updateUserProfile((current) => ({
			...current,
			starredDashboards: next ? current.starredDashboards.add(id) : current.starredDashboards.delete(id),
		}));
	};

	// starred -> can un-star
	if (profile.starredDashboards.has(id)) {
		return (
			<Tooltip>
				<TooltipTrigger>
					<Star
						{...props}
						onStarredChange={hasDeleteFavoriteDashboardsPermission ? onStarredChange : undefined}
						starred={true}
					/>
				</TooltipTrigger>

				{hasDeleteFavoriteDashboardsPermission ? (
					<TooltipContent>
						Remove from <LinkToStarredDashboards>starred dashboards</LinkToStarredDashboards>
					</TooltipContent>
				) : (
					<TooltipContent>You don't have permission to un-star this dashboard</TooltipContent>
				)}
			</Tooltip>
		);
	}

	// not starred -> can star

	return (
		<Tooltip>
			<TooltipTrigger>
				<Star
					{...props}
					onStarredChange={hasCreateFavoriteDashboardsPermission ? onStarredChange : undefined}
					starred={false}
				/>
			</TooltipTrigger>

			{hasCreateFavoriteDashboardsPermission ? (
				<TooltipContent>
					Add to <LinkToStarredDashboards>starred dashboards</LinkToStarredDashboards>
				</TooltipContent>
			) : (
				<TooltipContent>You don't have permission to star this dashboard</TooltipContent>
			)}
		</Tooltip>
	);
};

const LinkToStarredDashboards: React.FC<React.PropsWithChildren> = ({ children }) => (
	<LinkTo
		className="text-[length:inherit]"
		onClick={(event) => event.stopPropagation()}
		search={({ timeRange, timestamp, detachedFilters }) => ({
			timeRange,
			timestamp,
			detachedFilters,
			dashboardFilter: DashboardFilter.Starred,
		})}
		theme="light"
		// avoid triggering anything else rather than navigation
		to={DASHBOARDS_OVERVIEW_PATH}
	>
		{children}
	</LinkTo>
);
